import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { UserRestService } from '@base/modules/rest/user/user-rest.service';
import { LOGIN_PAGE } from '@base/services/app-routes.service';
import { AppState } from '@base/store';
import { CorePreloadActions } from '@base/store/preload';
import { CoreUiSelectors } from '@base/store/ui';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap, skipWhile, take, tap } from 'rxjs/operators';
import { UserContext } from '@base/services/user-context';
import { KeycloakService } from 'keycloak-angular';

@Injectable({
  providedIn: 'root',
})
export class SecuredPagesCanActivateGuard implements CanActivate {
  loadingData$ = this.store.select(CoreUiSelectors.loadingData);

  constructor(private store: Store<AppState>,
              private router: Router,
              private userRestService: UserRestService,
              private userContext: UserContext,
              private keycloakService: KeycloakService) {
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    const parameters = this.userContext.parameters;

    // If Keycloak is enabled, rely on Keycloak authentication
    if (parameters && parameters.keycloakEnabled) {
      return this.handleKeycloakAuthentication(state);
    }

    // Original logic for non-Keycloak authentication
    return this.userRestService.isUserLoggedIn()
      .pipe(
        map(() => true),
        catchError(() => of(false)),
        tap((loggedIn: boolean) => {
          if (loggedIn) {
            this.store.dispatch(CorePreloadActions.LoadAppSecuredData());
          } else {
            this.router.navigate([LOGIN_PAGE], {queryParams: {redirectUrl: state.url}});
          }
        }),
        mergeMap(() => this.loadingData$),
        skipWhile(loading => loading),
        take(1),
        map(() => true)
      );
  }

  private handleKeycloakAuthentication(state: RouterStateSnapshot): Observable<boolean> {
    try {
      return new Observable(observer => {
        this.keycloakService.isLoggedIn().then(isLoggedIn => {
          if (isLoggedIn) {
            this.store.dispatch(CorePreloadActions.LoadAppSecuredData());
            this.loadingData$.pipe(
              skipWhile(loading => loading),
              take(1)
            ).subscribe(() => {
              observer.next(true);
              observer.complete();
            });
          } else {
            // Let KeycloakCanActivateGuard handle the login
            observer.next(true);
            observer.complete();
          }
        }).catch(error => {
          console.error('Error checking Keycloak authentication:', error);
          observer.next(true);
          observer.complete();
        });
      });
    } catch (error) {
      console.error('Error in Keycloak authentication check:', error);
      return of(true);
    }
  }
}
